import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import trioLogo from '@/assets/trioLogo.png';
import Loading from '@/components/general/Loading';
import { supabase } from '@/lib/supabase';
import * as Styles from '../SignIn/SignIn.styles';

const ResetPassword = (): JSX.Element => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<'success' | 'error'>('error');

  const handleSubmit = useCallback(async () => {
    if (!password || !confirmPassword) {
      setMessage('Please fill in both fields');
      setMessageType('error');
      return;
    }

    if (password !== confirmPassword) {
      setMessage('Passwords do not match');
      setMessageType('error');
      return;
    }

    if (password.length < 6) {
      setMessage('Password must be at least 6 characters');
      setMessageType('error');
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (error) {
      setMessage(error.message);
      setMessageType('error');
    } else {
      // Clear the must_reset_password flag if it was set
      await supabase.auth.updateUser({ data: { must_reset_password: false } }).catch(() => {});
      setMessage('Password updated successfully! Redirecting...');
      setMessageType('success');
      setTimeout(() => navigate('/home'), 2000);
    }
  }, [password, confirmPassword, navigate]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleSubmit]);

  return (
    <Styles.Grid>
      <Styles.ContentContainer>
        <Styles.Title>This is Zion</Styles.Title>
        <Styles.SubTitle>Set your new password</Styles.SubTitle>
      </Styles.ContentContainer>

      <Styles.SignInContainer>
        <Styles.Card>
          <Styles.Img src={trioLogo} alt="Trio's logo" />
          <Styles.FormTitle>Reset Password</Styles.FormTitle>
          <Styles.Paragraph>Enter your new password below.</Styles.Paragraph>

          {message && <Styles.ErrorMessageBox type={messageType}>{message}</Styles.ErrorMessageBox>}

          <Styles.Form>
            <Styles.Input
              type="password"
              name="password"
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            <Styles.Input
              type="password"
              name="confirmPassword"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />

            <Styles.SignInBtn type="button" onClick={handleSubmit}>
              {isLoading ? <Loading color="inherit" /> : <span>UPDATE PASSWORD</span>}
            </Styles.SignInBtn>
          </Styles.Form>
        </Styles.Card>
      </Styles.SignInContainer>
    </Styles.Grid>
  );
};

export default ResetPassword;
